import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import AccountSetupBanner from "@/components/AccountSetupBanner";
import Landing from "@/pages/Landing";
import Grove from "@/pages/Grove";
import IdeaDetail from "@/pages/IdeaDetail";
import PlantIdea from "@/pages/PlantIdea";
import Roots from "@/pages/Roots";
import Moderation from "@/pages/Moderation";
import Basket from "@/pages/Basket";
import TryCritique from "@/pages/TryCritique";
import TryCritiqueDetail from "@/pages/TryCritiqueDetail";
import BrandApply from "@/pages/BrandApply";
import BrandPricing from "@/pages/BrandPricing";
import SharedFolder from "@/pages/SharedFolder";
import GiftLists from "@/pages/GiftLists";
import GiftListDetail from "@/pages/GiftListDetail";
import SharedGiftList from "@/pages/SharedGiftList";
import AuthCallback from "@/pages/AuthCallback";
import EmbedTrusted from "@/pages/EmbedTrusted";
import HowItWorks from "@/pages/HowItWorks";
import Signup from "@/pages/Signup";
import Login from "@/pages/Login";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PickUsername from "@/pages/PickUsername";
import PlannerLayout from "@/components/planner/PlannerLayout";
import PlannerLanding from "@/pages/planner/PlannerLanding";
import PlannerHome from "@/pages/planner/PlannerHome";
import PlannerToday from "@/pages/planner/PlannerToday";
import PlannerWeek from "@/pages/planner/PlannerWeek";
import PlannerMeals from "@/pages/planner/PlannerMeals";
import PlannerShopping from "@/pages/planner/PlannerShopping";
import PlannerCalendar from "@/pages/planner/PlannerCalendar";
import PlannerShared from "@/pages/planner/PlannerShared";
import Footer from "@/components/Footer";
import { PrivacyPolicy, TermsOfService, AffiliateDisclosure } from "@/pages/Policies";
import { BasketProvider } from "@/context/BasketContext";
import SplashScreen from "@/components/SplashScreen";

/**
 * Legacy `/your-grove` route. "Your Grove" was renamed to "Saved" and moved
 * to live under the owner's own Roots page — Instagram-style. This tiny
 * component keeps old bookmarks + shared links working by redirecting to
 * `/roots/{me}?tab=saved`. Anonymous visitors bounce to the landing so they
 * can sign in first.
 */
function YourGroveRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/" replace />;
  return <Navigate to={`/roots/${user.user_id}?tab=saved`} replace />;
}

function AppRouter() {
  const location = useLocation();
  // Detect OAuth callback fragment synchronously
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const isEmbed = location.pathname.startsWith("/embed/");
  // Hide the persistent nudge banner on auth pages themselves (users are
  // literally in the middle of setting things up — nagging them there is
  // noise). Also on embed pages so third-party iframes stay clean.
  const isAuthRoute = /^\/(signup|login|verify-email|forgot-password|reset-password|pick-username)(\/|$)/.test(location.pathname);
  // Planner has its own self-contained chrome (sidebar + top bar) — the main
  // Willoah Navbar and AccountSetupBanner are hidden inside /planner/* to
  // preserve the peaceful, focused feel the design mockups call for. The
  // public marketing landing `/planner` and public `/planner/shared/:token`
  // both stay in this bucket for the same reason.
  const isPlannerRoute = location.pathname === "/planner" || location.pathname.startsWith("/planner/");
  const hideMainChrome = isEmbed || isPlannerRoute;
  return (
    <>
      {!hideMainChrome && <Navbar />}
      {!hideMainChrome && !isAuthRoute && <AccountSetupBanner />}
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/grove" element={<Grove />} />
        <Route path="/idea/:id" element={<IdeaDetail />} />
        <Route path="/plant" element={<PlantIdea />} />
        <Route path="/your-grove" element={<YourGroveRedirect />} />
        <Route path="/roots/:user_id" element={<Roots />} />
        <Route path="/moderation" element={<Moderation />} />
        <Route path="/basket" element={<Basket />} />
        <Route path="/try-and-critique" element={<TryCritique />} />
        <Route path="/try-and-critique/:id" element={<TryCritiqueDetail />} />
        <Route path="/apply" element={<BrandPricing />} />
        <Route path="/apply/form" element={<BrandApply />} />
        <Route path="/g/:token" element={<SharedFolder />} />
        <Route path="/gift-lists" element={<GiftLists />} />
        <Route path="/gift-lists/:id" element={<GiftListDetail />} />
        <Route path="/gl/:token" element={<SharedGiftList />} />
        <Route path="/embed/trusted" element={<EmbedTrusted />} />
        {/* Planner routes — public landing + shared view are open; the layout
            gated routes bounce unauth'd visitors to /signup?returnTo=… */}
        <Route path="/planner" element={<PlannerLanding />} />
        <Route path="/planner/shared/:token" element={<PlannerShared />} />
        <Route path="/planner/*" element={<PlannerLayout />}>
          <Route path="home" element={<PlannerHome />} />
          <Route path="today" element={<PlannerToday />} />
          <Route path="week" element={<PlannerWeek />} />
          <Route path="meals" element={<PlannerMeals />} />
          <Route path="calendar" element={<PlannerCalendar />} />
          <Route path="shopping" element={<PlannerShopping />} />
        </Route>
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/login" element={<Login />} />
        <Route path="/verify-email/:token" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/pick-username" element={<PickUsername />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/affiliate-disclosure" element={<AffiliateDisclosure />} />
        <Route path="*" element={<Landing />} />
      </Routes>
      {!hideMainChrome && <Footer />}
    </>
  );
}

function App() {
  return (
    <div className="App grain">
      <BrowserRouter>
        <AuthProvider>
          <BasketProvider>
            <SplashScreen />
            <AppRouter />
            <Toaster
              position="bottom-center"
              toastOptions={{
                style: {
                  background: "#2A2823",
                  color: "#FBF8F1",
                  border: "1px solid #B79256",
                  borderRadius: "9999px",
                  padding: "10px 20px",
                },
              }}
            />
          </BasketProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
